<!-- eslint-disable vue/no-v-html -->
<script lang="ts">
/**
 * Textarea input for longer user input strings (e.g. paragraphs of data)
 */
import type { SetupContext } from 'vue';
import { defineComponent } from 'vue';
import type { HekateDisplayField } from '@onshape/www-inertia';
import FieldErrorMessage from './FieldErrorMessage.vue';
import FieldUtil from './FieldUtil';

export default defineComponent({
  components: {
    FieldErrorMessage,
  },
  props: {
    field: {
      required: true,
      type: Object as unknown as () => HekateDisplayField,
    },
    modelValue: {
      required: true,
      type: String,
    },
  },
  emits: ['blur', 'focus', 'update:modelValue'],
  setup(props: { modelValue: string; field: HekateDisplayField }, context: SetupContext) {
    const bubbleModel = FieldUtil.bubbleModel({
      get: () => props.modelValue,
      emitter: context,
    }) as globalThis.WritableComputedRef<string, string>;

    return {
      bubbleModel,
    };
  },
});
</script>

<template>
  <div class="field-textarea field-container">
    <div
      :class="{
        'field-content': true,
        'field-content-is-validated': field.isValidated,
        'field-content-is-errored': field.errorMessages.length > 0,
      }"
    >
      <label :for="`${field.htmlName}-for-${field.id}`">
        <span v-html="$t(field.name, field.htmlName, field.label)" /><sup
          v-if="field.isRequired"
          class="required-indicator"
          title="This field is required"
          >*</sup
        >
      </label>
      <textarea
        :id="`${field.htmlName}-for-${field.id}`"
        v-model="bubbleModel"
        :html-name="field.htmlName"
        :required="field.isRequired"
        :disabled="field.isDisabled"
        @blur="$emit('blur', field)"
        @focus="$emit('focus', field)"
      ></textarea>
      <field-error-message :messages="field.errorMessages" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../../style/vars.scss' as vars;

.field-textarea {
  .field-content {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;

    textarea {
      height: 120px;
      margin-bottom: 8px;
      -webkit-font-smoothing: antialiased;
      -webkit-box-direction: normal;
      box-sizing: border-box;
      height: 120px;
      font-size: 17px;
      padding: 12px;
      border: 1px solid vars.$color-input-border;
      margin-bottom: 12px;
      font-family: 'Raleway', sans-serif;
      font-weight: 400;
    }
  }
}
</style>
